import React, { useEffect, useState } from 'react';

import gql from 'graphql-tag';
import { useMutation } from 'react-apollo';

import { changeTempPassword, changeTempPasswordVariables } from './__queries__';
import getPasswordStrength from './getPasswordStrength';
import ResetPasswordView from './view';

const CHANGE_PASSWORD = gql`
  mutation changeTempPassword($newPassword: String!) {
    changeTempPassword(newPassword: $newPassword) {
      success
      token
      message
      error
    }
  }
`;

const minimumLength = process.env.NODE_ENV === 'development' ? 4 : 8;

interface Props {
  onSuccess?: () => void;
}

const ResetPassword = ({ onSuccess }: Props) => {
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const [changeTempPasswordMutation, changeTempPasswordPayload] = useMutation<
    changeTempPassword,
    changeTempPasswordVariables
  >(CHANGE_PASSWORD);

  const passwordCheck = () => {
    const passwordRegEx = /^[0-9A-Za-z!@#$%^&*?_~]+$/;
    let error = '';

    if (password !== password.trim()) {
      error = 'There should be no spaces at the beginning and end of the password.';
    } else if (password.length < minimumLength) {
      error = 'Password is too short.';
    } else if (!passwordRegEx.test(password)) {
      error =
        'Only letters (a–z and A–Z), numbers (0-9), and allowed special characters can be used.';
    } else if (process.env.NODE_ENV === 'production' && getPasswordStrength(password) < 40) {
      error = 'Password is not strong enough. Try a combination of letters, numbers, and symbols.';
    }

    setPasswordError(error);
    return !error;
  };

  const confirmPasswordCheck = () => {
    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.');
      return false;
    }

    setConfirmPasswordError('');
    return true;
  };

  const changePasswordStrength = () => {
    if (password.length === 0) {
      setPasswordStrength('');
    } else if (password.length < minimumLength) {
      setPasswordStrength('Too Short');
    } else {
      let strength = 'Very Weak';
      const score = getPasswordStrength(password);

      if (score >= 80) {
        strength = 'Very Strong';
      } else if (score >= 60) {
        strength = 'Strong';
      } else if (score >= 40) {
        strength = 'Average';
      } else if (score >= 20) {
        strength = 'Weak';
      }

      setPasswordStrength(strength);
    }
  };

  const handlePasswordBlur = () => {
    if (password) {
      passwordCheck();
    }
  };

  useEffect(() => {
    if (!password) {
      setPasswordError('');
      setPasswordStrength('');
    } else {
      changePasswordStrength();
      if (password && passwordError) {
        passwordCheck();
      }
    }
  }, [password]);

  useEffect(() => {
    if (confirmPasswordError) {
      confirmPasswordCheck();
    }
  }, [confirmPassword]);

  const handleChangePassword = () => {
    const isPasswordPassed = passwordCheck();
    const isConfirmationPasswordPassed = confirmPasswordCheck();

    if (isPasswordPassed && isConfirmationPasswordPassed) {
      changeTempPasswordMutation({ variables: { newPassword: password } });
    }
  };

  useEffect(() => {
    if (changeTempPasswordPayload.data?.changeTempPassword.success && onSuccess) {
      onSuccess();
    }
  }, [changeTempPasswordPayload]);

  return (
    <ResetPasswordView
      loading={changeTempPasswordPayload.loading}
      error={changeTempPasswordPayload?.error?.message}
      password={password}
      setPassword={setPassword}
      passwordError={passwordError}
      handlePasswordBlur={handlePasswordBlur}
      confirmPassword={confirmPassword}
      setConfirmPassword={setConfirmPassword}
      confirmPasswordError={confirmPasswordError}
      passwordStrength={passwordStrength}
      handleChangePassword={handleChangePassword}
    />
  );
};

export default ResetPassword;
